import { useState, type FormEvent } from "react"

type GuidesProps = {
  authenticityToken: string
  data: GuidesData
}

type GuidesData = {
  members: readonly Guide[]
  selected_camp: GuideCamp | null
}

type GuideCamp = {
  id: string
  name: string
}

type Guide = {
  active: boolean
  id: string
  name: string
}

export function Guides({ authenticityToken, data }: GuidesProps) {
  const [guides, setGuides] = useState(data.members)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const selectedCamp = data.selected_camp
  const isSaving = savingId !== null

  async function createGuide(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedCamp || isSaving) return

    const form = event.currentTarget
    const formData = new FormData(form)
    const name = formData.get("name")?.toString().trim() ?? ""
    if (!name) return

    setError(null)
    setSavingId("new")

    try {
      const createdGuide = await saveGuide(
        "/api/guides",
        {
          accommodation_id: selectedCamp.id,
          active: formData.get("active") === "true",
          name,
        },
        authenticityToken,
      )

      setGuides((currentGuides) => sortGuides([...currentGuides, createdGuide]))
      form.reset()
    } catch {
      setError("Guide could not be saved.")
    } finally {
      setSavingId(null)
    }
  }

  async function updateGuide(guide: Guide, event: FormEvent<HTMLFormElement>) {
    const formData = new FormData(event.currentTarget)
    const name = formData.get("name")?.toString().trim() ?? ""
    if (!name) return

    setError(null)
    setSavingId(guide.id)

    try {
      const updatedGuide = await saveGuide(
        `/api/guides/${guide.id}`,
        {
          active: formData.get("active") === "true",
          name,
        },
        authenticityToken,
        "PATCH",
      )

      setGuides((currentGuides) =>
        sortGuides(currentGuides.map((currentGuide) =>
          currentGuide.id === updatedGuide.id ? updatedGuide : currentGuide,
        )),
      )
    } catch {
      setError("Guide could not be saved.")
    } finally {
      setSavingId(null)
    }
  }

  if (!selectedCamp) {
    return (
      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <p className="text-sm font-medium leading-5 text-[#817d76]">No Asilia camps are available.</p>
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <h2 className="text-lg font-bold leading-6 text-[#424242]">Add guide</h2>
        <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={createGuide}>
          <div className="min-w-[240px] flex-1">
            <label className="block text-sm font-bold leading-5 text-[#424242]" htmlFor="new_guide_name">
              Name
            </label>
            <input
              className="mt-1 h-10 w-full rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
              disabled={isSaving}
              id="new_guide_name"
              name="name"
              required
            />
          </div>
          <label className="flex h-10 items-center gap-2 text-sm font-bold leading-5 text-[#424242]">
            <input
              className="h-4 w-4 rounded border-[#c7c7c7] text-[#337ab7]"
              defaultChecked
              disabled={isSaving}
              name="active"
              type="checkbox"
              value="true"
            />
            Active
          </label>
          <button
            className="h-10 rounded bg-[#2b2b2b] px-4 text-sm font-bold leading-5 text-white hover:bg-[#424242] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSaving}
            type="submit"
          >
            Add guide
          </button>
        </form>
      </section>

      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <h2 className="text-lg font-bold leading-6 text-[#424242]">Guides at {selectedCamp.name}</h2>

        {guides.length ? (
          <div className="mt-4 flex flex-col divide-y divide-[#e4e4e4]">
            {guides.map((guide) => {
              const rowSaving = savingId === guide.id

              return (
                <article aria-label={`${guide.name} guide`} className="py-4" key={guide.id}>
                  <form
                    className="flex flex-wrap items-end gap-3"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void updateGuide(guide, event)
                    }}
                  >
                    <div className="min-w-[240px] flex-1">
                      <label
                        className="block text-sm font-bold leading-5 text-[#424242]"
                        htmlFor={`guide_${guide.id}_name`}
                      >
                        Name
                      </label>
                      <input
                        className="mt-1 h-10 w-full rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
                        defaultValue={guide.name}
                        disabled={isSaving}
                        id={`guide_${guide.id}_name`}
                        name="name"
                        required
                      />
                    </div>
                    <label className="flex h-10 items-center gap-2 text-sm font-bold leading-5 text-[#424242]">
                      <input
                        className="h-4 w-4 rounded border-[#c7c7c7] text-[#337ab7]"
                        defaultChecked={guide.active}
                        disabled={isSaving}
                        name="active"
                        type="checkbox"
                        value="true"
                      />
                      Active
                    </label>
                    <button
                      className="h-10 rounded border border-[#d5d5d5] bg-white px-4 text-sm font-bold leading-5 text-[#424242] hover:bg-[#f2f2f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isSaving}
                      type="submit"
                    >
                      {rowSaving ? "Saving" : "Save"}
                    </button>
                  </form>
                </article>
              )
            })}
          </div>
        ) : (
          <p className="mt-4 text-sm font-medium leading-5 text-[#817d76]">
            No guides have been added for this camp yet.
          </p>
        )}
      </section>

      {error ? (
        <p className="rounded border border-[#e0b4b4] bg-[#fff2f2] px-4 py-3 text-sm font-bold text-[#873230]">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function sortGuides(guides: readonly Guide[]) {
  return [...guides].sort((firstGuide, secondGuide) =>
    firstGuide.name.localeCompare(secondGuide.name, undefined, { sensitivity: "base" }) ||
      firstGuide.id.localeCompare(secondGuide.id),
  )
}

async function saveGuide(
  path: string,
  attributes: Record<string, boolean | string>,
  authenticityToken: string,
  method = "POST",
) {
  const response = await fetch(path, {
    body: JSON.stringify({ accommodation_guide: attributes }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method,
  })

  if (!response.ok) {
    throw new Error(`Guide save failed with ${response.status}`)
  }

  return (await response.json()) as Guide
}
